'use client';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useState, useEffect, useCallback, useRef, ComponentRef } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

import ChevronIcon from '@/fo/icons/base/icons/arrow_thin.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitDialogBase } from '@/fo/ui/kit/dialog/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import {
  PrevButton,
  NextButton,
  usePrevNextButtons
} from '@/fo/ui/product/picture/main';

import { Thumb } from '../components/Thumb/Thumb';

import { styles } from './UiKitLightboxBase.styles';
type TransformWrapperRef = ComponentRef<typeof TransformWrapper>;
export interface Image {
  id: string | number;
  type: 'image' | 'video';
  src: string;
  alt: string;
}

export interface UiKitLightboxBaseProps {
  className?: string;
  images: Image[];
  open: boolean;
  onOpenChange?: () => void;
  initialIndexSlide?: number;
}

const imageSize = 720;

export function UiKitLightboxBase({
  className,
  images,
  open,
  onOpenChange,
  initialIndexSlide = 0
}: UiKitLightboxBaseProps) {
  const t = useTranslations();
  const zoomImageRef = useRef<Record<number, TransformWrapperRef | null>>({});

  const slots = styles();
  const isDesktop = useMediaQuery('lg');
  const [selectedIndex, setSelectedIndex] = useState(initialIndexSlide);
  const [isZoom, setIsZoom] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: isDesktop ? 'center' : 'start',
      containScroll: false,
      loop: true
    },
    [WheelGesturesPlugin()]
  );
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    axis: isDesktop ? 'y' : 'x'
  });

  const onThumbClick = (index: number) => {
    if (!emblaApi) return;

    emblaApi.scrollTo(index);
  };

  const { onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);

  const onSelect = useCallback(() => {
    if (!emblaApi || !emblaThumbsApi) return;

    setSelectedIndex(emblaApi.selectedScrollSnap());

    emblaApi.scrollTo(emblaApi.selectedScrollSnap());
    emblaThumbsApi.scrollTo(emblaApi.selectedScrollSnap());
  }, [emblaApi, emblaThumbsApi]);

  useEffect(() => {
    if (!emblaApi || !emblaThumbsApi) return;
    onSelect();

    emblaApi.on('select', onSelect).on('reInit', onSelect);

    emblaApi.scrollTo(selectedIndex);
    emblaThumbsApi.scrollTo(selectedIndex);
    if (zoomImageRef.current) {
      Object.values(zoomImageRef.current).forEach((ref) => {
        if (ref) ref.resetTransform(200);
      });
    }

    return () => {
      emblaApi.off('select', onSelect).off('reInit', onSelect);
    };
  }, [emblaApi, emblaThumbsApi, onSelect, selectedIndex]);

  useEffect(() => {
    emblaApi?.reInit({ watchDrag: !isZoom });
  }, [emblaApi, isZoom]);

  return (
    <UiKitDialogBase
      open={open}
      onOpenChange={onOpenChange}
      className={slots.base({ className })}
      fullScreen
      buttonClassName={slots.closeButton()}
      buttonIconClassName={slots.closeIconButton()}
      title={t('detail-view-image')}
    >
      {images.length > 1 && (
        <div className={slots.thumbs({ zoom: isZoom })}>
          <UiKitButtonBase
            onClick={() => emblaApi?.scrollPrev()}
            className={slots.thumbArrow({
              className: 'rotate-90',
              hide: !emblaApi?.canScrollPrev()
            })}
            rounded="full"
            color="white"
          >
            <ChevronIcon className={slots.thumbArrowIcon()} />
          </UiKitButtonBase>

          <div
            ref={emblaThumbsRef}
            className={slots.thumbsViewport()}
          >
            <div className={slots.thumbsContainer()}>
              {images.map((image: Image, index) => (
                <Thumb
                  key={index}
                  onClick={() => onThumbClick(index)}
                  selected={index === selectedIndex}
                >
                  {image.type === 'video' && (
                    <video
                      className={slots.video()}
                      width={80}
                      height={80}
                    >
                      <source
                        src={image.src}
                        type="video/mp4"
                      />
                    </video>
                  )}
                  {image.type === 'image' && (
                    <Image
                      className={slots.thumb()}
                      src={image.src}
                      alt=""
                      width={80}
                      height={80}
                    />
                  )}
                </Thumb>
              ))}
            </div>
          </div>

          <UiKitButtonBase
            onClick={() => emblaApi?.scrollNext()}
            className={slots.thumbArrow({
              className: '-rotate-90',
              hide: !emblaApi?.canScrollNext()
            })}
            rounded="full"
            color="white"
          >
            <ChevronIcon className={slots.thumbArrowIcon()} />
          </UiKitButtonBase>
        </div>
      )}

      <div
        className={slots.viewport({ zoom: isZoom })}
        ref={emblaRef}
      >
        <div className={slots.container({ zoom: isZoom })}>
          {images.map((image: Image, index) => (
            <div
              className={slots.imageWrapper({ zoom: isZoom })}
              key={index}
              onClick={() => {
                if (isDesktop) {
                  setIsZoom(false);
                }
              }}
            >
              {image.type === 'video' && (
                <video
                  className={slots.video()}
                  autoPlay
                  muted
                  width={imageSize}
                  height={imageSize}
                >
                  <source
                    src={image.src}
                    type="video/mp4"
                  />
                </video>
              )}
              {image.type === 'image' && (
                <TransformWrapper
                  ref={(elem) => {
                    zoomImageRef.current[index] = elem;
                  }}
                  initialScale={1}
                  maxScale={2}
                  doubleClick={{ mode: 'toggle' }}
                  wheel={{ disabled: true }}
                  onPanningStart={() => {
                    emblaApi?.reInit({ watchDrag: false });
                  }}
                  onPanningStop={() => {
                    emblaApi?.reInit({ watchDrag: true });
                  }}
                  panning={{
                    disabled: false
                  }}
                >
                  {({ zoomIn, resetTransform, instance }) => (
                    <TransformComponent
                      wrapperStyle={{
                        height: '100%',
                        width: '100%',
                        justifyContent: 'center',
                        touchAction: 'none'
                      }}
                      contentStyle={{
                        height: '100%',
                        width: '100%',
                        justifyContent: 'center'
                      }}
                    >
                      <Image
                        className={slots.image({ zoom: isZoom })}
                        src={image.src}
                        alt=""
                        width={imageSize}
                        height={imageSize}
                        onClick={() => {
                          if (isDesktop) {
                            return false;
                          }

                          const isTransformActive =
                            instance.transformState.scale > 1;

                          if (isTransformActive) {
                            resetTransform();
                          } else {
                            zoomIn(2);
                          }

                          emblaApi?.reInit({ watchDrag: isTransformActive });
                          setIsZoom(!isTransformActive);
                        }}
                      />
                    </TransformComponent>
                  )}
                </TransformWrapper>
              )}
            </div>
          ))}
        </div>
        {images.length > 1 && (
          <>
            <PrevButton onClick={onPrevButtonClick} />
            <NextButton onClick={onNextButtonClick} />
          </>
        )}
      </div>
    </UiKitDialogBase>
  );
}
